import { DAY, MINUTE } from './constants';

const summarizeHistory = (records, now, target) => {
  const valid = records.filter((record) => record.at <= now);
  const visits = valid.filter((record) => record.kind === 'visit' && record.at < Math.floor(now / DAY) * DAY);
  const attempts = valid.filter((record) => record.kind === 'attempt' && record.at > now - 30 * 60000);
  const bookings = valid.filter((record) => record.kind === 'booking');
  return {
    source: 'localStorageAndMemory',
    payLaterAttemptsLast30Minutes: attempts.filter((record) => record.payLater === true).length,
    distinctStaysLast30Minutes: new Set(attempts.map(({ start, end }) => `${start}|${end}`)).size,
    distinctOccupationsLast30Minutes: new Set(attempts.map(({ occupation }) => occupation).filter(Boolean)).size,
    distinctRoomCountsLast30Minutes: new Set(attempts.map(({ rooms }) => rooms).filter(Boolean)).size,
    previousVisitDays: new Set(visits.map((record) => Math.floor(record.at / DAY))).size,
    qualifiedVisitDays: new Set(
      visits.filter((record) => record.qualified).map((record) => Math.floor(record.at / DAY)),
    ).size,
    oldestObservationAgeMs: valid.length ? Math.max(0, now - Math.min(...valid.map((record) => record.at))) : null,
    attemptsLast30Minutes: valid.filter((record) => record.kind === 'attempt' && record.at > now - 30 * 60000).length,
    knownBookings: bookings.length,
    cancelledBookings: bookings.filter((record) => record.state === 'cancelled').length,
    establishedBookings: bookings.filter((record) => record.state === 'confirmed' && record.createdAt < now - DAY)
      .length,
    searches: valid.filter((record) => record.kind === 'search').length,
    selections: valid.filter((record) => record.kind === 'selection').length,
    distinctContactsForSelection: new Set(
      valid
        .filter(
          (record) => record.kind === 'attempt' && record.target === target.target && record.at > now - 30 * 60000,
        )
        .map((record) => record.contact)
        .filter(Boolean),
    ).size,
  };
};

const getActivity = ({ interaction, navigation, journey, observation, context }) => {
  const currentActivation =
    interaction.contextMatches !== false &&
    interaction.activations > 0 &&
    interaction.lastActivationAgeMs !== null &&
    interaction.lastActivationAgeMs <= 10 * MINUTE &&
    (navigation.checkoutAt === null || interaction.lastActivationAt >= navigation.checkoutAt);
  const checkout =
    interaction.contextMatches !== false &&
    interaction.distinctFields >= 2 &&
    interaction.trustedActions >= 3 &&
    interaction.spanMs >= 1000 &&
    interaction.edits + interaction.pastes + interaction.composition > 0 &&
    interaction.lastEditAt !== null &&
    interaction.lastEditAt > context.evaluatedAt - 10 * MINUTE;
  const selected = journey.selection;
  const matches = selected?.key === context.journeyKey && journey.contextKey === context.browsingKey;
  const browser = (journey.browserRecent || []).filter(
    ({ at }) => selected && at <= selected.at && at > selected.at - 10 * MINUTE,
  );
  const browserSpan = browser.length ? browser.at(-1).at - browser[0].at : 0;
  const recent =
    browser.length > 0 &&
    browser.at(-1).at <= context.evaluatedAt &&
    browser.at(-1).at > context.evaluatedAt - 10 * MINUTE;
  const coherent =
    matches &&
    navigation.checkoutAt !== null &&
    selected.at <= navigation.checkoutAt &&
    browser.length >= 2 &&
    browser.some(({ stage }) => stage === 'rates') &&
    browserSpan >= 1000 &&
    recent;
  const initialized = ['cache', 'schema', 'session'].some((source) => interaction.sources[source] > 0);
  const form = journey.formEvidence;
  const restoredForm =
    navigation.checkoutAt !== null &&
    initialized &&
    currentActivation &&
    observation.activeMs >= 1000 &&
    form?.key === context.journeyKey &&
    form.fields >= 2 &&
    form.edits > 0 &&
    form.spanMs >= 1000 &&
    form.at <= context.evaluatedAt &&
    form.at > context.evaluatedAt - 10 * MINUTE;
  const hydratedJourney = initialized && coherent && currentActivation && observation.activeMs >= 1000;
  const reasons = [];
  if (!checkout && !hydratedJourney && !restoredForm) {
    if (!selected) reasons.push('missingSelectionEvidence');
    else if (!matches) reasons.push('journeyContextMismatch');
    if (!currentActivation) reasons.push('missingCurrentActivation');
    if (!recent && browser.length) reasons.push('expiredInteractionEvidence');
    if (browser.length < 2 || browserSpan < 1000) reasons.push('insufficientDistinctActions');
    if (!initialized) reasons.push('missingInitializationEvidence');
    if (navigation.checkoutAt === null) reasons.push('missingCheckoutEvidence');
    if (selected && navigation.checkoutAt !== null && selected.at > navigation.checkoutAt)
      reasons.push('selectionAfterCheckout');
    if (observation.activeMs < 1000) reasons.push('insufficientActiveObservation');
    if (form && form.at <= context.evaluatedAt - 10 * MINUTE && !reasons.includes('expiredInteractionEvidence'))
      reasons.push('expiredInteractionEvidence');
  }
  return {
    checkout,
    hydratedJourney,
    restoredForm,
    qualified: checkout || hydratedJourney || restoredForm,
    sustained: checkout || hydratedJourney || restoredForm,
    reasons,
  };
};

const classify = (signals, risks, threshold) => {
  const { interaction, observation, authentication, history, navigation, environment, context } = signals;
  const evidence = [];
  const add = (id, category, weight) => evidence.push({ id, category, weight });
  const activity = getActivity(signals);
  if (activity.checkout) add('observedInteraction', 'interaction', 30);
  if (!activity.checkout && activity.hydratedJourney) add('observedJourneyInteraction', 'interaction', 30);
  if (!activity.checkout && !activity.hydratedJourney && activity.restoredForm)
    add('restoredFormInteraction', 'interaction', 30);
  if (activity.sustained) add('sustainedInteraction', 'interaction', 20);
  if (activity.qualified && observation.activeMs >= 1000) add('activeObservation', 'observation', 10);
  if (context.validStay) add('validBookingContext', 'context', 5);
  if (authentication.sessionPresent) add('sessionPresent', 'authentication', 10);
  if (authentication.sessionPresent && authentication.loginObserved) add('loginObserved', 'authentication', 10);
  if (history.qualifiedVisitDays > 0) add('returningVisitor', 'history', 10);
  if (history.establishedBookings > 0) add('establishedBookingHistory', 'history', 10);
  if (navigation.orderedJourney) add('observedJourney', 'navigation', 10);
  if (environment.automated === true) add('declaredAutomation', 'environment', -60);
  if (environment.headlessDeclared === true)
    add('headlessDeclared', 'environment', environment.automated === true ? 0 : -60);
  if (interaction.synthetic >= 5 && interaction.trustedActions < 3)
    add('syntheticInteractionDominant', 'interaction', -20);
  risks.forEach(({ id, category, weight }) => add(id, category, -weight));
  const score = Math.min(
    100,
    Math.max(
      0,
      evidence.reduce((sum, item) => sum + item.weight, 0),
    ),
  );
  const trusted =
    activity.qualified &&
    context.validStay &&
    environment.automated !== true &&
    environment.headlessDeclared !== true &&
    score >= threshold;
  const reasons = evidence.map(({ id }) => id);
  if (!activity.qualified) reasons.push('insufficientInteractionEvidence', ...activity.reasons);
  if (!context.validStay) reasons.push('invalidOrMissingBookingContext');
  if (!trusted && activity.qualified && score < threshold) reasons.push('confidenceBelowThreshold');
  return { trusted, score, reasons, evidence };
};

export { classify, getActivity, summarizeHistory };
